/**
 * What is playing, as reactive state.
 *
 * The {@link Deck} is imperative and lives outside the reactive graph; these
 * signals are the projection of it that the UI renders. They are written only
 * from deck events, the render loop and user gestures — never from a
 * component body.
 */

import { createSignal } from 'solid-js';
import { Deck } from '../audio/deck.ts';
import type { Artwork, RepeatMode, Track } from '../types/media.ts';

export const deck = new Deck();

export const [track, setTrack] = createSignal<Track | null>(null);
export const [artwork, setArtwork] = createSignal<Artwork | null>(null);
export const [position, setPosition] = createSignal(0);
export const [duration, setDuration] = createSignal(0);
export const [playing, setPlaying] = createSignal(false);
export const [volume, setVolume] = createSignal(0.85);
export const [muted, setMuted] = createSignal(false);
export const [shuffle, setShuffle] = createSignal(false);
export const [repeat, setRepeat] = createSignal<RepeatMode>('off');
export const [level, setLevel] = createSignal(0);
export const [queue, setQueue] = createSignal<readonly Track[]>([]);

/** RPM and pitch as shown on the deck readout. */
export const [rpm, setRpm] = createSignal(33);
export const [pitch, setPitch] = createSignal(0);

/** True while the user is dragging the scrubber, so live updates stand back. */
export const [scrubbing, setScrubbing] = createSignal(false);
/** The position the scrubber is previewing, in seconds. */
export const [scrubPosition, setScrubPosition] = createSignal(0);

/** Playback fraction, honouring an in-progress scrub. */
export function progress(): number {
  const d = duration();
  if (!d) return 0;
  const p = scrubbing() ? scrubPosition() : position();
  return Math.min(Math.max(p / d, 0), 1);
}

/** The position to display — the scrub preview while dragging. */
export const displayPosition = (): number => (scrubbing() ? scrubPosition() : position());
